import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import Box from "@mui/material/Box";
import Tab, { TabProps } from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { SoftphoneSipConfig, UserPermissions } from "../hooks/useSoftphoneConfig";
import { getTeamsSsoToken } from "../hooks/teamsSso";
import { useTeamsUser } from "../hooks/useTeamsUser";
import { useMakeCall } from "../hooks/useMakeCall";
import { useFavorites } from "../hooks/useFavorites";
import { normalizeRegistrarHost, pabxFetch } from "../utils/pabxApi";
import { AuthorizedFetch } from "../utils/tenantAdminApi";
import { pabxFavoriteToContact, teamsFavoriteToContact } from "../utils/favoriteMapping";
import { FavoriteSource, PabxContactItem, TeamsContactItem } from "../types/contacts";
import { DialConfirmationDialog, DialIconButton, MISSING_NUMBER_PLACEHOLDER } from "./DialControls";
import { useTranslation, TranslateFn, renderMessage } from "../i18n/LocaleContext";
import { getRuntimeEnv } from "../config/runtimeEnv";

interface ContactSearchProps {
  sipConfig: SoftphoneSipConfig | null;
  utechApiToken: string;
  permissions: UserPermissions | null;
  onPlaceCall: (number: string, variaveis?: string) => void;
  /** Envelope autenticado do bootstrap (token SSO + repetição no 401), para os favoritos. */
  authorizedFetch?: AuthorizedFetch;
  className?: string;
}

type ActiveTab = "favorites" | "pabx" | "ms-teams";

function readString(value: unknown) {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  return "";
}

function readFirstValue(source: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = readString(source[key]);
    if (value) return value;
  }
  return "";
}

function extractContactsArray(payload: unknown): Record<string, unknown>[] {
  if (!payload || typeof payload !== "object") return [];
  const record = payload as Record<string, unknown>;
  const contactlist = record.contactlist;
  if (contactlist && typeof contactlist === "object") {
    const contacts = (contactlist as Record<string, unknown>).contacts;
    if (contacts && typeof contacts === "object" && !Array.isArray(contacts)) {
      return Object.entries(contacts).reduce<Record<string, unknown>[]>((accumulator, [id, value]) => {
        if (!value || typeof value !== "object") return accumulator;
        accumulator.push({ id, ...(value as Record<string, unknown>) });
        return accumulator;
      }, []);
    }
  }
  return [];
}

function extractTeamsContactsArray(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) {
    return payload.filter((item): item is Record<string, unknown> => !!item && typeof item === "object");
  }

  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.value)) {
    return record.value.filter((item): item is Record<string, unknown> => !!item && typeof item === "object");
  }

  return [];
}

function readContactNumber(numbers: Record<string, unknown> | undefined, key: string) {
  if (!numbers) return "";
  const entry = numbers[key];
  if (entry && typeof entry === "object") {
    return readString((entry as Record<string, unknown>).number);
  }
  return readString(entry);
}

// `id` fica nulo quando a origem não o traz, em vez de receber um id sintético com a posição na
// lista: aquela chave mudava a cada busca e não serve para guardar um favorito. O item aparece
// normalmente, só sem estrela (ver renderContactCard).
function mapContactResult(item: Record<string, unknown>, t: TranslateFn): PabxContactItem | null {
  const name = readFirstValue(item, ["name", "nome", "displayName", "contactName", "description"]);
  const numbers = item.numbers && typeof item.numbers === "object"
    ? item.numbers as Record<string, unknown>
    : undefined;
  const businessNumber = readContactNumber(numbers, "business");
  const mobileNumber = readContactNumber(numbers, "mobile");
  const extensionNumber = readContactNumber(numbers, "extension");
  const homeNumber = readContactNumber(numbers, "home");

  if (!name && !businessNumber && !mobileNumber && !extensionNumber && !homeNumber) return null;

  return {
    id: readFirstValue(item, ["id", "contactId", "uuid"]) || null,
    name: name || t("contactSearch.noNameContact"),
    businessNumber: businessNumber || MISSING_NUMBER_PLACEHOLDER,
    mobileNumber: mobileNumber || MISSING_NUMBER_PLACEHOLDER,
    extensionNumber: extensionNumber || MISSING_NUMBER_PLACEHOLDER,
    homeNumber: homeNumber || MISSING_NUMBER_PLACEHOLDER,
  };
}

function mapTeamsContactResult(item: Record<string, unknown>, t: TranslateFn): TeamsContactItem | null {
  const displayName = readFirstValue(item, ["displayName", "name", "givenName", "surname", "userPrincipalName"]);
  const mobilePhone = readString(item.mobilePhone) || MISSING_NUMBER_PLACEHOLDER;
  const businessPhones = Array.isArray(item.businessPhones)
    ? item.businessPhones.map((value) => readString(value)).filter(Boolean).slice(0, 3)
    : [];

  if (!displayName && mobilePhone === MISSING_NUMBER_PLACEHOLDER && businessPhones.length === 0) {
    return null;
  }

  return {
    id: readFirstValue(item, ["id", "contactId", "userId"]) || null,
    displayName: displayName || t("contactSearch.noNameContact"),
    mobilePhone,
    businessPhones: [0, 1, 2].map((position) => businessPhones[position] || MISSING_NUMBER_PLACEHOLDER),
  };
}

function buildContactSearchUrl(registrar: string, filterdata: string, token: string) {
  const host = normalizeRegistrarHost(registrar);
  if (!host) return "";
  return `https://${host}/utech/v1/contacts/contactlist?token=${token}&page=0&filterdata=${encodeURIComponent(filterdata)}&filterfields=name,number`;
}

function buildTeamsBotBaseUrl() {
  const baseUrl = (getRuntimeEnv("REACT_APP_TEAMS_BOT_API_BASE_URL") || "").trim();
  if (baseUrl) {
    return baseUrl.replace(/\/+$/, "");
  }

  if (typeof window === "undefined") {
    return "";
  }

  return window.location.origin;
}

// O Graph pagina `contacts` de 10 em 10 por padrão; 100 faz a primeira leva aparecer rápido sem
// multiplicar as requisições de quem tem muitos contatos.
const TEAMS_CONTACTS_PAGE_SIZE = 100;

// Teto defensivo: se o gateway devolvesse nextLink indefinidamente, o laço não giraria para
// sempre. Expresso em contatos, e não em páginas, para que o limite real não mude junto se o
// tamanho da página for calibrado.
const MAX_TEAMS_CONTACTS = 5000;
const MAX_TEAMS_CONTACTS_PAGES = Math.ceil(MAX_TEAMS_CONTACTS / TEAMS_CONTACTS_PAGE_SIZE);

// `userId`/`tenantId` não são enviados de propósito: sob /api/bearer o serviço tira a identidade
// do token SSO e ignora a query. Mandá-los sugeriria, falsamente, que dá para pedir os contatos
// de outra pessoa.
//
// Também não vai `$filter`: a busca da aba é filtro local sobre a lista inteira. Isso importa
// além do desempenho — é o que autoriza a reconciliação dos favoritos a concluir que um contato
// ausente da resposta foi apagado (ver reconcileTeams).
function buildTeamsUserContactsUrl() {
  const baseUrl = buildTeamsBotBaseUrl();
  if (!baseUrl) {
    return "";
  }

  return `${baseUrl}/api/bearer/userContacts/page?$top=${TEAMS_CONTACTS_PAGE_SIZE}`;
}

// O gateway devolve o nextLink como caminho RELATIVO (`/api/bearer/...`). Concatenar preserva um
// eventual prefixo de path na base configurada, que `new URL(link, base)` descartaria.
function resolveTeamsContactsNextUrl(nextLink: unknown) {
  if (typeof nextLink !== "string" || !nextLink) {
    return null;
  }

  if (/^https?:\/\//i.test(nextLink)) {
    return nextLink;
  }

  const baseUrl = buildTeamsBotBaseUrl();
  return `${baseUrl}${nextLink.startsWith("/") ? nextLink : `/${nextLink}`}`;
}

function sortTeamsContacts(items: TeamsContactItem[]) {
  return [...items].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" })
  );
}

function normalizeSearch(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

// Aba desabilitada com tooltip. O Tabs injeta props de aba (value, textColor, fullWidth,
// indicator, selectionFollowsFocus…) no seu filho direto; aqui elas são repassadas ao Tab, em vez
// de caírem no <span> que o Tooltip exige em volta de um elemento desabilitado — onde viravam
// atributos inválidos no DOM. O span continua sendo o item flex ao lado das outras abas, então o
// `flex: 1 1 auto` do variant="fullWidth" fica nele.
function DisabledTabWithTooltip({ title, ...tabProps }: TabProps & { title: string }) {
  return (
    <Tooltip title={title} placement="top" arrow>
      <Box component="span" sx={{ flex: "1 1 auto", display: "flex" }}>
        <Tab {...tabProps} disabled sx={{ width: "100%" }} />
      </Box>
    </Tooltip>
  );
}

const ContactSearch: React.FC<ContactSearchProps> = ({ sipConfig, utechApiToken, permissions, onPlaceCall, authorizedFetch, className }) => {
  const { t } = useTranslation();
  const teamsUser = useTeamsUser();
  // Backend antigo (permissions ausente) mantém o acesso liberado.
  const canAccessPabxContacts = permissions?.pabxContactsAccess ?? true;
  // Favoritos é a aba inicial — fixa, e não condicionada ao conteúdo da lista: fazê-la depender de
  // a lista estar vazia faria a aba trocar debaixo do usuário quando a resposta do GET chegasse.
  // Sendo estática, também não dispara carga de rede nenhuma na abertura do app.
  const [activeTab, setActiveTab] = useState<ActiveTab>("favorites");
  const [pabxSearchTerm, setPabxSearchTerm] = useState("");
  const [pabxSearchResults, setPabxSearchResults] = useState<PabxContactItem[]>([]);
  const [pabxSearchLoading, setPabxSearchLoading] = useState(false);
  const [pabxSearchError, setPabxSearchError] = useState("");
  const [pabxSearchNonce, setPabxSearchNonce] = useState(0);
  const [favoritesSearchTerm, setFavoritesSearchTerm] = useState("");
  const [teamsSearchTerm, setTeamsSearchTerm] = useState("");
  const [teamsContacts, setTeamsContacts] = useState<TeamsContactItem[]>([]);
  const [teamsContactsLoading, setTeamsContactsLoading] = useState(false);
  const [teamsContactsError, setTeamsContactsError] = useState("");
  const [teamsContactsFetchedKey, setTeamsContactsFetchedKey] = useState("");
  // Pedido explícito de carga fora da aba Teams (botão Atualizar da aba Favoritos). Abrir a aba
  // Favoritos **não** basta: a carga termina em reconciliação, que remove favoritos, e remover é
  // ato do usuário — não efeito colateral de navegar.
  const [teamsLoadRequested, setTeamsLoadRequested] = useState(false);
  const { selectedNumber, dialLoading, dialError, requestDial, cancelDial, confirmDial } = useMakeCall(
    sipConfig,
    onPlaceCall
  );
  const teamsSsoTokenRef = useRef<string | null>(null);

  const {
    favorites,
    max: favoritesMax,
    total: favoritesTotal,
    isFull: favoritesFull,
    loading: favoritesLoading,
    error: favoritesError,
    isFavorite,
    togglePabxFavorite,
    toggleTeamsFavorite,
    reload: reloadFavorites,
    reconcileTeams,
    reconcilePabx,
  } = useFavorites(authorizedFetch, true);

  // A permissão chega de forma assíncrona (após o check-in); se negada enquanto a aba
  // PABX já estava ativa, muda para a aba Teams em vez de deixar a aba desabilitada selecionada.
  useEffect(() => {
    if (!canAccessPabxContacts) {
      setActiveTab((current) => (current === "pabx" ? "ms-teams" : current));
    }
  }, [canAccessPabxContacts]);

  const trimmedPabxSearchTerm = pabxSearchTerm.trim();
  const trimmedTeamsSearchTerm = teamsSearchTerm.trim();
  const trimmedFavoritesSearchTerm = favoritesSearchTerm.trim();
  const registrarUrl = useMemo(
    () => buildContactSearchUrl(sipConfig?.domain || "", trimmedPabxSearchTerm, utechApiToken),
    [sipConfig?.domain, trimmedPabxSearchTerm, utechApiToken]
  );
  const teamsUserContactsUrl = useMemo(() => buildTeamsUserContactsUrl(), []);
  const filteredTeamsContacts = useMemo(() => {
    if (!trimmedTeamsSearchTerm) {
      return teamsContacts;
    }

    const normalizedQuery = normalizeSearch(trimmedTeamsSearchTerm);
    return teamsContacts.filter((contact) => {
      const searchable = normalizeSearch([
        contact.displayName,
        contact.mobilePhone,
        ...contact.businessPhones,
      ].join(" "));
      return searchable.includes(normalizedQuery);
    });
  }, [teamsContacts, trimmedTeamsSearchTerm]);

  // A aba Favoritos é servida do estado local, sem rede: as duas origens são convertidas para o
  // formato de contato e ordenadas por nome. Os favoritos do PABX ficam ocultos quando a permissão
  // está negada — escondidos, não apagados: a permissão é reversível e religá-la devolve a lista.
  const favoriteEntries = useMemo(() => {
    const entries: { source: FavoriteSource; contact: PabxContactItem | TeamsContactItem; name: string }[] = [];

    if (canAccessPabxContacts) {
      for (const favorite of favorites.pabx) {
        const contact = pabxFavoriteToContact(favorite);
        entries.push({ source: "pabx", contact, name: contact.name });
      }
    }
    for (const favorite of favorites.teams) {
      const contact = teamsFavoriteToContact(favorite);
      entries.push({ source: "teams", contact, name: contact.displayName });
    }

    entries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    if (!trimmedFavoritesSearchTerm) {
      return entries;
    }

    const normalizedQuery = normalizeSearch(trimmedFavoritesSearchTerm);
    return entries.filter((entry) => {
      const numbers = entry.source === "pabx"
        ? [
            (entry.contact as PabxContactItem).businessNumber,
            (entry.contact as PabxContactItem).mobileNumber,
            (entry.contact as PabxContactItem).extensionNumber,
            (entry.contact as PabxContactItem).homeNumber,
          ]
        : [
            (entry.contact as TeamsContactItem).mobilePhone,
            ...(entry.contact as TeamsContactItem).businessPhones,
          ];
      return normalizeSearch([entry.name, ...numbers].join(" ")).includes(normalizedQuery);
    });
  }, [canAccessPabxContacts, favorites, trimmedFavoritesSearchTerm]);

  function renderPhoneNumber(number: string) {
    return <DialIconButton number={number} onDial={requestDial} />;
  }

  // Estrela de favorito: ponto único, usado pelas três abas. Um contato sem `id` não a recebe —
  // não há chave estável para guardá-lo. Com a lista cheia, só a estrela VAZADA é desabilitada:
  // desmarcar precisa continuar funcionando, e é como o usuário abre vaga.
  function renderFavoriteToggle(source: FavoriteSource, contact: PabxContactItem | TeamsContactItem) {
    if (!contact.id) {
      return null;
    }

    const marked = isFavorite(source, contact.id);
    const blocked = !marked && favoritesFull;
    const label = marked
      ? t("contactSearch.favorites.remove")
      : blocked
        ? t("contactSearch.favorites.full", { max: favoritesMax })
        : t("contactSearch.favorites.add");

    const button = (
      <IconButton
        size="small"
        aria-label={label}
        disabled={blocked || !authorizedFetch}
        onClick={() => (source === "pabx"
          ? togglePabxFavorite(contact as PabxContactItem)
          : toggleTeamsFavorite(contact as TeamsContactItem))}
      >
        {marked ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}
      </IconButton>
    );

    // O Tooltip não dispara sobre elemento desabilitado: o span intermediário é obrigatório.
    return (
      <Tooltip title={label} placement="top" arrow>
        {blocked ? <Box component="span" sx={{ display: "inline-flex" }}>{button}</Box> : button}
      </Tooltip>
    );
  }

  // Um cartão por contato, com uma linha rótulo/número por número preenchido. Números
  // ausentes não viram linha — quatro rótulos com "---" por contato é só ruído.
  function renderContactCard(
    key: string,
    name: string,
    numbers: [string, string][],
    favorite?: { source: FavoriteSource; contact: PabxContactItem | TeamsContactItem; showSource?: boolean }
  ) {
    const filled = numbers.filter(([, number]) => number && number !== MISSING_NUMBER_PLACEHOLDER);

    return (
      <article className="contact-card" key={key}>
        <div className="contact-card-header">
          <h3 className="contact-card-name">{name}</h3>
          {favorite?.showSource && (
            <span className="contact-card-source">
              {favorite.source === "pabx" ? t("contactSearch.tab.pabx") : t("contactSearch.tab.teams")}
            </span>
          )}
          {favorite && renderFavoriteToggle(favorite.source, favorite.contact)}
        </div>
        {filled.length === 0 ? (
          <p className="contact-card-empty">{t("contactSearch.noNumbers")}</p>
        ) : (
          <dl className="contact-card-numbers">
            {filled.map(([label, number]) => (
              <div className="contact-card-row" key={label}>
                <dt>{label}</dt>
                <dd>{renderPhoneNumber(number)}</dd>
              </div>
            ))}
          </dl>
        )}
      </article>
    );
  }

  useEffect(() => {
    if (trimmedPabxSearchTerm.length < 3) {
      setPabxSearchResults([]);
      setPabxSearchLoading(false);
      setPabxSearchError("");
      return;
    }

    if (!sipConfig?.domain || !registrarUrl) {
      setPabxSearchResults([]);
      setPabxSearchLoading(false);
      setPabxSearchError(t("contactSearch.error.domainNotReady"));
      return;
    }

    const controller = new AbortController();

    async function fetchContacts() {
      try {
        setPabxSearchLoading(true);
        setPabxSearchError("");
        const response = await pabxFetch("contactList", registrarUrl, {
          method: "GET",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = await response.json();
        const results = extractContactsArray(payload)
          .map((item) => mapContactResult(item, t))
          .filter((item): item is PabxContactItem => item !== null);
        setPabxSearchResults(results);
        // Atualização oportunista da cópia dos favoritos do PABX: aproveita os contatos que esta
        // busca trouxe. Nunca remove — sem busca por id, um favorito ausente do resultado é
        // indistinguível de um que não casou com o termo digitado.
        void reconcilePabx(results);
      } catch (error) {
        if (controller.signal.aborted) return;
        setPabxSearchResults([]);
        // Busca direta no PABX: a URL da requisição leva o token na query string, então a
        // mensagem do erro (que pode citá-la) não é exibida.
        setPabxSearchError(t("contactSearch.error.fetchFailedGeneric"));
      } finally {
        if (!controller.signal.aborted) {
          setPabxSearchLoading(false);
        }
      }
    }

    fetchContacts();
    return () => controller.abort();
  }, [registrarUrl, sipConfig?.domain, trimmedPabxSearchTerm, pabxSearchNonce, reconcilePabx, t]);

  useEffect(() => {
    // Duas entradas, e só duas: a aba de contatos do Teams (carga preguiçosa, uma vez por
    // documento) e um pedido explícito do botão Atualizar. A conclusão desta carga é o que
    // dispara a reconciliação dos favoritos — por isso ela não roda por conta própria.
    if (activeTab !== "ms-teams" && !teamsLoadRequested) {
      return;
    }

    if (teamsUser.status === "loading") {
      setTeamsContactsLoading(true);
      setTeamsContactsError(t("contactSearch.teams.waitingContext"));
      return;
    }

    if (teamsUser.status !== "ready" || !teamsUser.user?.userId || !teamsUser.user?.tenantId) {
      teamsSsoTokenRef.current = null;
      setTeamsContacts([]);
      setTeamsContactsLoading(false);
      setTeamsContactsError(t("contactSearch.teams.invalidContext"));
      return;
    }

    if (!teamsUserContactsUrl) {
      setTeamsContacts([]);
      setTeamsContactsLoading(false);
      setTeamsContactsError(t("contactSearch.teams.serviceNotConfigured"));
      return;
    }

    // A chave só é gravada quando a última página chega, então ela sozinha diz se a carga
    // terminou. Consultar `teamsContacts.length` aqui reiniciaria o efeito a cada página
    // acumulada — abortando o laço em curso — e, de quebra, fazia quem não tem contato algum
    // recarregar a cada entrada na aba.
    const fetchKey = `${teamsUser.user.userId}:${teamsUser.user.tenantId}`;
    if (teamsContactsFetchedKey === fetchKey) {
      return;
    }

    let disposed = false;
    const controller = new AbortController();
    setTeamsContactsLoading(true);
    setTeamsContactsError("");

    // Carga progressiva: a lista é baixada por inteiro, mas cada página é renderizada assim que
    // chega, em vez de o usuário esperar a última. A busca é filtro local, então continua
    // operando sobre o que já carregou.
    const loadAllPages = async () => {
      const accessToken = await getTeamsSsoToken();
      teamsSsoTokenRef.current = accessToken;

      let nextUrl: string | null = teamsUserContactsUrl;
      let accumulated: TeamsContactItem[] = [];
      let pages = 0;

      while (nextUrl && pages < MAX_TEAMS_CONTACTS_PAGES) {
        const response: Response = await fetch(nextUrl, {
          method: "GET",
          signal: controller.signal,
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status} ${response.statusText}`);
        }

        const payload = await response.json();
        if (disposed) return;

        const mapped = extractTeamsContactsArray(payload)
          .map((item) => mapTeamsContactResult(item, t))
          .filter((item): item is TeamsContactItem => item !== null);

        // `accumulated` começa vazio, então a primeira página substitui o que houvesse de uma
        // carga anterior e as seguintes acumulam sobre ela.
        accumulated = sortTeamsContacts([...accumulated, ...mapped]);
        setTeamsContacts(accumulated);

        nextUrl = resolveTeamsContactsNextUrl(payload?.["@odata.nextLink"]);
        pages += 1;
      }

      setTeamsContactsFetchedKey(fetchKey);

      // Autorização para a reconciliação REMOVER: a carga terminou por falta de nextLink, e não
      // porque bateu no teto de páginas. Truncada, a lista não prova que um contato deixou de
      // existir — e apagaria todo favorito a partir do contato 5001. Uma falha em qualquer página
      // cai no catch abaixo e não chega aqui. A requisição nunca leva `$filter`, então uma lista
      // vazia e completa é resposta a ser levada a sério: zera os favoritos do Teams.
      const complete = nextUrl === null;
      void reconcileTeams(accumulated, { complete });
    };

    void loadAllPages()
      .catch((error) => {
        if (disposed || controller.signal.aborted) return;

        teamsSsoTokenRef.current = null;
        setTeamsLoadRequested(false);
        // O que já chegou permanece na tela: descartar as páginas válidas porque uma falhou
        // deixaria o usuário sem nada, e a busca local ainda serve sobre a lista parcial. A
        // reconciliação NÃO roda — ter contatos na lista não é prova de carga completa.
        setTeamsContactsFetchedKey("");
        setTeamsContactsError(t("contactSearch.teams.loadFailedGeneric"));
      })
      .finally(() => {
        if (!disposed) {
          setTeamsContactsLoading(false);
          // O pedido foi atendido (com ou sem sucesso): a próxima carga precisa de um novo clique.
          setTeamsLoadRequested(false);
        }
      });

    return () => {
      disposed = true;
      controller.abort();
    };
  }, [activeTab, teamsLoadRequested, teamsContactsFetchedKey, teamsUser.status, teamsUser.user?.tenantId, teamsUser.user?.userId, teamsUserContactsUrl, reconcileTeams, t]);

  const isFavoritesTab = activeTab === "favorites";
  const isPabxTab = activeTab === "pabx";
  const isTeamsTab = activeTab === "ms-teams";
  const activeSearchTerm = isFavoritesTab ? favoritesSearchTerm : isPabxTab ? pabxSearchTerm : teamsSearchTerm;
  const activeTrimmedSearchTerm = isFavoritesTab
    ? trimmedFavoritesSearchTerm
    : isPabxTab ? trimmedPabxSearchTerm : trimmedTeamsSearchTerm;

  const setActiveSearchTerm = (value: string) => {
    if (isFavoritesTab) return setFavoritesSearchTerm(value);
    if (isPabxTab) return setPabxSearchTerm(value);
    return setTeamsSearchTerm(value);
  };

  // Botão Atualizar: o ÚNICO gatilho de carga nova. Não há refresh automático — a reconciliação
  // remove favoritos, e remover é ato do usuário, não efeito colateral de voltar de uma suspensão.
  const refreshing = isFavoritesTab
    ? favoritesLoading || teamsContactsLoading
    : isPabxTab ? pabxSearchLoading : teamsContactsLoading;
  const refreshDisabled = refreshing
    || (isPabxTab && trimmedPabxSearchTerm.length < 3)
    || (isFavoritesTab && !authorizedFetch);

  const handleRefresh = useCallback(() => {
    if (isPabxTab) {
      // Repete a busca atual: o termo não mudou, então é o nonce que reinicia o efeito.
      setPabxSearchNonce((current) => current + 1);
      return;
    }
    // Zerar a chave é o que faz o carregador rebaixar a lista inteira — e, no fim dela, a
    // reconciliação rodar sobre dado fresco.
    setTeamsContactsFetchedKey("");
    if (isFavoritesTab) {
      // Fora da aba Teams o efeito só roda a pedido, e é este o pedido.
      setTeamsLoadRequested(true);
      void reloadFavorites();
    }
  }, [isFavoritesTab, isPabxTab, reloadFavorites]);

  return (
    <article className={`status-card call-history-card contact-search-card${className ? ` ${className}` : ""}`}>
      <div className="card-header">
        <h2>{t("contactSearch.title")}</h2>
        {favoritesMax > 0 && isFavoritesTab && (
          <span className="contact-favorites-count">
            {t("contactSearch.favorites.count", { total: favoritesTotal, max: favoritesMax })}
          </span>
        )}
      </div>
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        variant="fullWidth"
        sx={{ px: 2, pt: 0.5, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab value="favorites" label={t("contactSearch.tab.favorites")} />
        {canAccessPabxContacts ? (
          <Tab value="pabx" label={t("contactSearch.tab.pabx")} />
        ) : (
          <DisabledTabWithTooltip
            value="pabx"
            label={t("contactSearch.tab.pabx")}
            title={t("contactSearch.tab.pabx.noPermission")}
          />
        )}
        <Tab value="ms-teams" label={t("contactSearch.tab.teams")} />
      </Tabs>
      <div className="call-search-panel-body">
        <div style={{ marginBottom: 12 }}>
          <small>
            {isFavoritesTab
              ? t("contactSearch.hint.favorites")
              : isPabxTab
                ? t("contactSearch.hint.pabx")
                : t("contactSearch.hint.teams")}
          </small>
        </div>
        <div className="call-control-field-inline" style={{ marginBottom: 16 }}>
          <TextField
            value={activeSearchTerm}
            onChange={(e) => setActiveSearchTerm(e.target.value)}
            placeholder={isFavoritesTab
              ? t("contactSearch.placeholder.favorites")
              : isPabxTab ? t("contactSearch.placeholder.pabx") : t("contactSearch.placeholder.teams")}
            size="small"
            variant="outlined"
            sx={{ minWidth: 200 }}
          />
          <Tooltip title={t("contactSearch.refresh")} placement="top" arrow>
            <Box component="span" sx={{ display: "inline-flex" }}>
              <Button
                variant="outlined"
                disabled={refreshDisabled}
                onClick={handleRefresh}
                aria-label={t("contactSearch.refresh")}
              >
                {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              </Button>
            </Box>
          </Tooltip>
          <Button
            variant="outlined"
            disabled={!activeTrimmedSearchTerm}
            onClick={() => setActiveSearchTerm("")}
            aria-label={t("contactSearch.clearAria")}
          >
            <DeleteIcon />
          </Button>
        </div>
        {favoritesError && (
          <p className="call-search-placeholder call-search-placeholder-error">
            {renderMessage(t, favoritesError)}
          </p>
        )}
        <div className="call-search-results" aria-live="polite">
          {isFavoritesTab && favoriteEntries.length === 0 && (
            <p className="call-search-placeholder">
              {trimmedFavoritesSearchTerm
                ? t("contactSearch.favorites.noResults", { term: favoritesSearchTerm })
                : t("contactSearch.favorites.empty")}
            </p>
          )}
          {isFavoritesTab && favoriteEntries.length > 0 && (
            <div className="contact-card-list">
              {favoriteEntries.map((entry) => (entry.source === "pabx"
                ? renderContactCard(
                    `pabx:${(entry.contact as PabxContactItem).id}`,
                    (entry.contact as PabxContactItem).name,
                    [
                      [t("contactSearch.column.business"), (entry.contact as PabxContactItem).businessNumber],
                      [t("contactSearch.column.mobile"), (entry.contact as PabxContactItem).mobileNumber],
                      [t("contactSearch.column.extension"), (entry.contact as PabxContactItem).extensionNumber],
                      [t("contactSearch.column.home"), (entry.contact as PabxContactItem).homeNumber],
                    ],
                    { source: "pabx", contact: entry.contact, showSource: true }
                  )
                : renderContactCard(
                    `teams:${(entry.contact as TeamsContactItem).id}`,
                    (entry.contact as TeamsContactItem).displayName,
                    [
                      [t("contactSearch.column.mobile"), (entry.contact as TeamsContactItem).mobilePhone],
                      [t("contactSearch.column.business1"), (entry.contact as TeamsContactItem).businessPhones[0]],
                      [t("contactSearch.column.business2"), (entry.contact as TeamsContactItem).businessPhones[1]],
                      [t("contactSearch.column.business3"), (entry.contact as TeamsContactItem).businessPhones[2]],
                    ],
                    { source: "teams", contact: entry.contact, showSource: true }
                  )))}
            </div>
          )}
          {isPabxTab && trimmedPabxSearchTerm.length < 3 && (
            <p className="call-search-placeholder">
              {t("contactSearch.minChars")}
            </p>
          )}
          {isPabxTab && trimmedPabxSearchTerm.length >= 3 && pabxSearchLoading && (
            <p className="call-search-placeholder">
              {t("contactSearch.searching", { term: pabxSearchTerm })}
            </p>
          )}
          {isPabxTab && trimmedPabxSearchTerm.length >= 3 && !pabxSearchLoading && !!pabxSearchError && (
            <p className="call-search-placeholder call-search-placeholder-error">
              {pabxSearchError}
            </p>
          )}
          {isPabxTab && trimmedPabxSearchTerm.length >= 3 && !pabxSearchLoading && !pabxSearchError && pabxSearchResults.length === 0 && (
            <p className="call-search-placeholder">
              {t("contactSearch.noResults", { term: pabxSearchTerm })}
            </p>
          )}
          {isPabxTab && !pabxSearchLoading && !pabxSearchError && pabxSearchResults.length > 0 && (
            <div className="contact-card-list">
              {pabxSearchResults.map((item, index) =>
                renderContactCard(item.id || `pabx-result-${index}`, item.name, [
                  [t("contactSearch.column.business"), item.businessNumber],
                  [t("contactSearch.column.mobile"), item.mobileNumber],
                  [t("contactSearch.column.extension"), item.extensionNumber],
                  [t("contactSearch.column.home"), item.homeNumber],
                ], { source: "pabx", contact: item })
              )}
            </div>
          )}
          {isTeamsTab && teamsContactsLoading && (
            <p className="call-search-placeholder" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CircularProgress size={16} />
              {t("contactSearch.teams.loading")}
            </p>
          )}
          {isTeamsTab && !teamsContactsLoading && !!teamsContactsError && (
            <p className="call-search-placeholder call-search-placeholder-error">
              {teamsContactsError}
            </p>
          )}
          {isTeamsTab && !teamsContactsLoading && !teamsContactsError && filteredTeamsContacts.length === 0 && (
            <p className="call-search-placeholder">
              {t("contactSearch.teams.noResults", {
                suffix: trimmedTeamsSearchTerm ? t("contactSearch.teams.noResultsSuffix", { term: teamsSearchTerm }) : "",
              })}
            </p>
          )}
          {/* A lista aparece durante a carga e junto do erro, não só no fim: cada página entra na tela
              assim que chega, e uma falha no meio mantém à vista o que já foi baixado (a busca local
              continua servindo sobre a lista parcial). */}
          {isTeamsTab && filteredTeamsContacts.length > 0 && (
            <div className="contact-card-list">
              {filteredTeamsContacts.map((item, index) =>
                renderContactCard(item.id || `teams-result-${index}`, item.displayName, [
                  [t("contactSearch.column.mobile"), item.mobilePhone],
                  [t("contactSearch.column.business1"), item.businessPhones[0]],
                  [t("contactSearch.column.business2"), item.businessPhones[1]],
                  [t("contactSearch.column.business3"), item.businessPhones[2]],
                ], { source: "teams", contact: item })
              )}
            </div>
          )}
        </div>
      </div>
      <DialConfirmationDialog
        sipConfig={sipConfig}
        selectedNumber={selectedNumber}
        dialLoading={dialLoading}
        dialError={dialError}
        onCancel={cancelDial}
        onConfirm={confirmDial}
      />
    </article>
  );
};

export default ContactSearch;
